import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";

import { Row, Col, Form, Button, Container, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { CHAT_API } from "../../../api/api";

import { FaSearch } from "react-icons/fa";
import { IoSend } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { CgAttachment } from "react-icons/cg";
import { HiPencil } from "react-icons/hi";

import albumImage from "../../../assets/icons/student/album.svg";
import PLACEHOLDER_IMG from "../../../assets/student/place-holder.png";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./AdminChats.css";

const AdminChats = () => {
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [messages, setMessages] = useState([]);
  const [pauseChat, setPauseChat] = useState(false);
  const [messageText, setMessageText] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);

  // Filter states
  const [selectedTutorFilter, setSelectedTutorFilter] = useState("All Tutors");
  const [selectedCourseFilter, setSelectedCourseFilter] =
    useState("All Courses");
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState("All Students");

  // search states
  const [searchTerm, setSearchTerm] = useState("");

  //loading
  const [loadingAllChats, setloadingAllChats] = useState(true);
  const [loadingSendMsg, setloadingSendMsg] = useState(false);
  const [loadingPause, setloadingPause] = useState(false);

  const fileInputRef = useRef();
  const messagesEndRef = useRef();
  const currentUserRole = "admin";

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const handleAttach = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileData = {
      file, // <-- raw File object from input
      preview: URL.createObjectURL(file),
      type: file.type,
      name: file.name,
    };

    setSelectedFile(fileData);
    // Clear text input if a file is selected
    setMessageText("");
  };

  // Cleanup object URL (memory leak fix)
  useEffect(() => {
    return () => {
      if (selectedFile?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(selectedFile.preview);
      }
    };
  }, [selectedFile]);

  const fetchStudents = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingAllChats(false);
      navigate("/admin-signin");

      return;
    }

    try {
      const response = await axios.post(
        CHAT_API.GET_CHAT_LIST,
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setStudents(data.chatList || []);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setloadingAllChats(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Click student → fetch messages
  const handleStudentClick = async (student) => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      navigate("/admin-signin");
      return;
    }

    setSelectedStudent(student);

    // Clear input & file when switching tutor
    setMessageText("");
    setSelectedFile(null);
    setEditingMessageId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!student.chat_room_id) {
      // No chat room yet → empty messages
      setMessages([]);
      setPauseChat(false);
      return;
    }

    try {
      const response = await axios.post(
        CHAT_API.GET_MESSAGES,
        { user_id: userId, chat_room_id: student.chat_room_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        const getFileType = (fileName = "") => {
          const ext = fileName.split(".").pop()?.toLowerCase();

          if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
            return "image";
          }

          return "file";
        };

        const formattedMessages = (data.messages || []).map((msg) => ({
          chatId: msg.chat_id,
          fromRole: msg.role?.trim().toLowerCase(),
          fullName: msg.full_name,
          text: msg.message,
          time: msg.to_char,
          edited: msg.edited,
          file: msg.file_url
            ? {
                preview: msg.file_url,
                name: msg.file
                  ? msg.file.split("/").pop().replace(/^\d+_/, "")
                  : "File",
                type: getFileType(msg.file),
              }
            : null,
        }));

        setMessages(formattedMessages);
        setPauseChat(Boolean(data.pause_chat)); // true or false from API

        await fetchStudents();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Msg error:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    }
  };

  // Scroll to bottom on messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const handleTutorSendMsg = async () => {
    if (!selectedStudent) {
      showErrorToast("Please select a conversation first.");
      return;
    }

    if (!messageText.trim() && !selectedFile) return;

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingSendMsg(false);
      navigate("/admin-signin");

      return;
    }

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("student_id", selectedStudent.student_id);
    formData.append("course_id", selectedStudent.course_id);
    formData.append("message", messageText);

    if (selectedFile?.file) {
      formData.append("file_url", selectedFile.file);
    }

    setloadingSendMsg(true);
    try {
      const response = await axios.post(CHAT_API.SEND_MESSAGE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.statusCode === 200) {
        // If chat_room_id was null → refresh page
        if (!selectedStudent.chat_room_id) {
          window.location.reload();
          return;
        } else await handleStudentClick(selectedStudent);

        setMessageText("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error send message:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setloadingSendMsg(false);
    }
  };

  const handleShowEditMsg = (msg) => {
    setMessageText(msg.text || "");
    setSelectedFile(msg.file || null);
    setEditingMessageId(msg.chatId);

    setTimeout(() => {
      document.querySelector(".admin-doubts-input")?.focus();
    }, 0);
  };

  // Edit message
  const handleUpdateTutorSendMsg = async () => {
    if (!editingMessageId) {
      showErrorToast("No message selected to edit.");
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      setloadingSendMsg(false);
      navigate("/admin-signin");

      return;
    }

    const formData = new FormData();
    formData.append("chat_id", editingMessageId);
    formData.append("message", messageText);

    if (selectedFile?.file) {
      formData.append("file_url", selectedFile.file); // key matches backend
    }

    setloadingSendMsg(true);
    try {
      const response = await axios.post(CHAT_API.UPDATE_MESSAGE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.statusCode === 200) {
        await handleStudentClick(selectedStudent);

        setEditingMessageId(null);
        setMessageText("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error update message:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setloadingSendMsg(false);
    }
  };

  const handleToggleChatStatus = async () => {
    if (!selectedStudent?.chat_room_id) return; // no chat room, nothing to pause

    const token = localStorage.getItem("token");

    if (!token) {
      setloadingPause(false);
      navigate("/admin-signin");

      return;
    }

    setloadingPause(true);
    try {
      const response = await axios.post(
        CHAT_API.UPDATE_PAUSE_CHAT,
        {
          chat_room_id: selectedStudent.chat_room_id,
          pause_chat: !pauseChat,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        await handleStudentClick(selectedStudent);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating chat status:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setloadingPause(false);
    }
  };

  const filteredTutorsForDropdown = useMemo(
    () => [...new Set(students.map((s) => s.tutor_name))],
    [students],
  );

  const filteredCoursesForDropdown = useMemo(() => {
    return selectedTutorFilter === "All Tutors"
      ? [...new Set(students.map((s) => s.course_title))]
      : [
          ...new Set(
            students
              .filter((s) => s.tutor_name === selectedTutorFilter)
              .map((s) => s.course_title),
          ),
        ];
  }, [students, selectedTutorFilter]);

  const filteredStudentsForDropdown = useMemo(() => {
    return selectedTutorFilter === "All Tutors" &&
      selectedCourseFilter === "All Courses"
      ? [...new Set(students.map((s) => s.student_name))]
      : [
          ...new Set(
            students
              .filter(
                (s) =>
                  (selectedTutorFilter === "All Tutors" ||
                    s.tutor_name === selectedTutorFilter) &&
                  (selectedCourseFilter === "All Courses" ||
                    s.course_title === selectedCourseFilter),
              )
              .map((s) => s.student_name),
          ),
        ];
  }, [students, selectedTutorFilter, selectedCourseFilter]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  // Filter students based on course, and search term
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      if (
        selectedTutorFilter !== "All Tutors" &&
        s.tutor_name !== selectedTutorFilter
      )
        return false;
      if (
        selectedCourseFilter !== "All Courses" &&
        s.course_title !== selectedCourseFilter
      )
        return false;
      if (
        selectedStudentFilter !== "All Students" &&
        s.student_name !== selectedStudentFilter
      )
        return false;
      if (
        normalizedSearch &&
        !(
          s.student_name.toLowerCase().includes(normalizedSearch) ||
          s.course_title.toLowerCase().includes(normalizedSearch)
        )
      )
        return false;
      return true;
    });
  }, [
    students,
    selectedTutorFilter,
    selectedCourseFilter,
    selectedStudentFilter,
    normalizedSearch,
  ]);

  if (loadingAllChats) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="admin-internshipsss-management-wrapper">
      <h4 className="tutor-feedback-title">Chat Conversation</h4>
      <p className="tutor-feedback-subtitle">
        View chat conversations, review messages, and monitor communication
      </p>

      <div className="admin-doubts-root-section-divssss mb-4">
        <Row className="admin-doubts-totalboxss">
          {/* Left: Student List & Filters */}
          <Col md={4} className="admin-doubts-left">
            <div className="admin-doubts-filters-wrapper mb-3 d-flex gap-2">
              {/* tutor Filter */}
              <Form.Select
                value={selectedTutorFilter}
                onChange={(e) => setSelectedTutorFilter(e.target.value)}
                className="admin-doubts-filter-select"
              >
                <option value="All Tutors">All Tutors</option>
                {filteredTutorsForDropdown.map((tutor, idx) => (
                  <option key={idx} value={tutor}>
                    {tutor}
                  </option>
                ))}
              </Form.Select>

              {/* Course Filter */}
              <Form.Select
                value={selectedCourseFilter}
                onChange={(e) => setSelectedCourseFilter(e.target.value)}
                className="admin-doubts-filter-select"
              >
                <option value="All Courses">All Courses</option>
                {filteredCoursesForDropdown.map((course, idx) => (
                  <option key={idx} value={course}>
                    {course}
                  </option>
                ))}
              </Form.Select>

              {/* student Filter */}
              <Form.Select
                value={selectedStudentFilter}
                onChange={(e) => setSelectedStudentFilter(e.target.value)}
                className="admin-doubts-filter-select"
              >
                <option value="All Students">All Students</option>
                {filteredStudentsForDropdown.map((student, idx) => (
                  <option key={idx} value={student}>
                    {student}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Search */}
            <div className="admin-doubts-search-wrap mb-3">
              <input
                type="text"
                placeholder="Search by student name or course"
                className="admin-doubts-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="admin-doubts-search-icon">
                <FaSearch />
              </span>
            </div>

            {/* Student List */}
            <div className="admin-doubts-list">
              {filteredStudents.length ? (
                filteredStudents.map((s) => (
                  <div
                    key={`${s.student_id}-${s.course_id}`}
                    className={`admin-doubts-list-item ${
                      selectedStudent?.student_id === s.student_id &&
                      selectedStudent?.course_id === s.course_id
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleStudentClick(s)}
                  >
                    <div className="admin-doubts-initials">
                      {initialsFromName(s.student_name)}
                    </div>

                    <div className="admin-doubts-meta">
                      <div className="admin-doubts-meta-top">
                        <span
                          className="admin-doubts-name"
                          title={s.student_name}
                        >
                          {s.student_name}
                          <span
                            className="tutor-doubts-course-badge"
                            title={s.course_title}
                          >
                            ({s.course_title})
                          </span>
                        </span>
                        {/* <span className="admin-doubts-time">{s.lastUpdated}</span> */}
                      </div>

                      <div
                        className="admin-doubts-snippet"
                        // title={s.last_message}
                      >
                        {s.last_message}
                      </div>
                    </div>

                    {Number(s.unseen_count) > 0 && (
                      <div className="admin-doubts-unread">
                        {s.unseen_count}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="tutor-admin-doubts-empty text-center p-4 mt-5">
                  <img
                    src={PLACEHOLDER_IMG}
                    alt="no queries"
                    className="tutor-admin-doubts-img-fluidsss"
                  />
                  <p className="mt-3 tutor-admin-doubts-img-bottom-text">
                    No results found
                  </p>
                  <p className="mt-0 tutor-admin-doubts-img-bottom-para">
                    Try searching with a different name or keyword
                  </p>
                </div>
              )}
            </div>
          </Col>

          {/* Right: Chat Area */}
          <Col md={8} className="admin-doubts-right p-0">
            {selectedStudent ? (
              <>
                {/* Header */}
                <div className="admin-doubts-right-header">
                  <div className="d-flex align-items-center gap-3">
                    <div className="admin-doubts-header-initials">
                      {initialsFromName(selectedStudent.student_name)}
                    </div>

                    <div>
                      <div className="admin-doubts-header-name">
                        {selectedStudent.student_name}
                      </div>
                      <div className="admin-doubts-header-role">
                        {" "}
                        {selectedStudent.course_title}
                      </div>
                    </div>
                  </div>

                  <Button
                    className="admin-doubts-header-role-btn"
                    onClick={handleToggleChatStatus}
                    disabled={loadingPause}
                    style={{
                      backgroundColor: loadingPause
                        ? "#888" // gray while loading
                        : pauseChat
                          ? "#0C6CF2" // gray  if paused
                          : "#888", // blue if active
                      color: "#fff",
                    }}
                  >
                    {loadingPause ? (
                      <Spinner animation="border" size="sm" />
                    ) : pauseChat ? (
                      "Active Chat "
                    ) : (
                      "Chat Paused"
                    )}
                  </Button>
                </div>

                {/* Chat Messages */}
                <div className="admin-doubts-chat-area">
                  {messages?.map((m) => (
                    <div
                      key={m.chatId}
                      className={`d-flex flex-column mb-3 ${
                        m.fromRole === currentUserRole
                          ? "align-items-end"
                          : "align-items-start"
                      }`}
                    >
                      {m.edited && (
                        <div className="student-doubts-edited-label">
                          Edited
                        </div>
                      )}
                      <div className={`admin-doubts-msg ${m.fromRole}`}>
                        {/* MESSAGE CONTENT */}
                        <div className="admin-doubts-msg-text">
                          {m.text && <div>{m.text}</div>}

                          {m.file && (
                            <>
                              {m.file?.type === "image" ? (
                                <a
                                  href={m.file.preview}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img
                                    src={m.file.preview}
                                    alt="sent-img"
                                    style={{
                                      maxWidth: "200px",
                                      borderRadius: "8px",
                                      marginTop: "5px",
                                    }}
                                  />
                                </a>
                              ) : (
                                <div className="file-box">
                                  <a
                                    href={m.file.preview}
                                    download={m.file.name}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <CgAttachment /> {m.file.name}
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* FOOTER */}
                        <div className="admin-doubts-msg-footer">
                          <span className="msg-role-label">
                            {m.fromRole === "student"
                              ? "By Student"
                              : m.fromRole === "tutor"
                                ? m.fullName
                                : "Admin"}
                          </span>

                          <div className="admin-doubts-msg-footer-div-two">
                            <span className="admin-doubts-msg-time">
                              {m.time}
                            </span>

                            {m.fromRole === "admin" && (
                              <span
                                className="ms-2 edit-btn"
                                onClick={() => handleShowEditMsg(m)}
                              >
                                <HiPencil />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Bar */}
                {pauseChat ? (
                  <div className="admin-chat-paused-box">
                    ⚠ Chat has been paused by admin. Messaging is disabled.
                  </div>
                ) : (
                  <div className="admin-doubts-input-bar">
                    <div className="admin-doubts-input-inner">
                      {selectedFile && (
                        <div className="inline-file-preview">
                          <span className="file-name" title={selectedFile.name}>
                            <CgAttachment /> {selectedFile.name}
                          </span>
                          <span
                            className="remove-file"
                            onClick={() => {
                              setSelectedFile(null);
                              setMessageText("");
                              if (fileInputRef.current)
                                fileInputRef.current.value = "";
                            }}
                            title="Remove file"
                          >
                            <RxCross2 />
                          </span>
                        </div>
                      )}

                      <Form.Control
                        className="admin-doubts-input"
                        placeholder={
                          editingMessageId
                            ? "Edit your message..."
                            : selectedFile
                              ? "File selected — cannot type message"
                              : "Type a message..."
                        }
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            editingMessageId
                              ? handleUpdateTutorSendMsg()
                              : handleTutorSendMsg();
                          }
                        }}
                        autoComplete="off"
                        disabled={!!selectedFile}
                      />

                      <img
                        src={albumImage}
                        alt="attach"
                        className="admin-doubts-attach-icon"
                        onClick={() => fileInputRef.current?.click()}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={handleAttach}
                      />
                    </div>

                    <Button
                      className="admin-doubts-send-btn"
                      onClick={() =>
                        editingMessageId
                          ? handleUpdateTutorSendMsg()
                          : handleTutorSendMsg()
                      }
                      disabled={loadingSendMsg}
                    >
                      {loadingSendMsg ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <IoSend />
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="tutor-admin-doubts-empty text-center p-4 mt-5">
                <img
                  src={PLACEHOLDER_IMG}
                  alt="no queries"
                  className="tutor-admin-doubts-img-fluidsss"
                />
                <p className="mt-3 tutor-admin-doubts-img-bottom-text">
                  Select a conversation.
                </p>
                <p className="mt-0 tutor-admin-doubts-img-bottom-para">
                  Choose a conversation to view messages and respond.{" "}
                </p>
              </div>
            )}
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default AdminChats;
